import { Box } from '@mui/material';
import { CustomStyleCompactTable } from '../../src/components/Styles';
import NotInformation from "./NotInformation";
import React from "react";

/**
 * @component CustomCompactTable
 * @description
 * Reusable compact table component that renders a list of items using a custom
 * render function. Supports custom row styling, row status configuration,
 * search result handling, and empty state visualization.
 *
 * @param {Object[]} data Array of records to display.
 * @param {Function} render Function responsible for rendering the content of each row. Receives the current row as parameter.
 * @param {Boolean} searchElement Enables the "no results found" state when a search filter is applied.
 * @param {React.ReactNode} childrenNotInformation Optional custom content displayed inside the empty state component.
 * @param {String} notInformationLegend Message displayed when no data is available.
 * @param {Function} getRowClassName Optional callback that returns a custom CSS class name for each row. Receives (index, row) as parameters.
 * @param {Function} getRowStatusConfig Optional callback that returns a status configuration object used by
 * CustomStyleCompactTable.row().
 * Receives the current row as parameter.
 * @param {Function} getRowKey Optional callback that returns a stable key for each row. Receives (row, index) as parameters.
 * Defaults to the index.
 * @returns {JSX.Element}
 *
 *
 * @Author      Susana García Rodríguez
 * @JobTitle    Frontend Developer
 * @Email       sgarcia@ibl.mx
 */
export const CustomCompactTable = ({ data = [], render, searchElement, childrenNotInformation, notInformationLegend, getRowClassName, getRowStatusConfig, getRowKey}) => {
    return (
        <Box>
            { data.length > 0 ?
                data.map((row, index) => {
                    const statusConfig = getRowStatusConfig?.(row);
                    return (
                        <Box id={row?.id} key={getRowKey ? getRowKey(row, index) : index} sx={CustomStyleCompactTable.row(index, data.length, statusConfig)}
                            className={`row ${getRowClassName?.(index, row) || ''}`}>
                            {render(row)}
                        </Box>
                    );
                })
                :
                <NotInformation
                    text={notInformationLegend}
                    card={true}
                    searchElement={searchElement} //activates 'no_results_found' when applied
                >
                    {childrenNotInformation}
                </NotInformation>
            }
        </Box>
    );
};
